import React, { useContext, useImperativeHandle, useState, useCallback } from 'react';
import { UserRepository } from '@amityco/ts-sdk';
import { useHistory } from 'react-router';
import { arrowForwardCircle, closeCircle } from 'ionicons/icons';
import {
  useIonRouter,
  IonButtons,
  IonButton,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonIcon,
  IonItem,
  IonLabel,
} from '@ionic/react';
import { deleteUser, signout } from '../../global/request/auth';
import { deleteUserData } from '../../global/request/user';
import { resetUser, trackEvent } from '../../utils/customerio';
import { AuthContext, AuthDispatchContext } from '../authentication/AuthContext';
import toggleDevice from '../notifications/ToggleDevice';
import ContactUsForm from '../userFeedback/ContactUsForm';
import DeleteAccountModal from './DeleteAccountModal';

import '../../styles/settingsModal/SettingsModal.scss';

function SettingsModal(props, ref) {
  const router = useIonRouter();
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const authCtx = useContext(AuthContext);
  const authDispatchCtx = useContext(AuthDispatchContext);

  const handleSignout = useCallback(async () => {
    // Track logout event before clearing auth
    trackEvent('user_logout', {
      userId: authCtx.idData.sub,
      logoutMethod: 'manual',
    });

    // Reset user in Customer.io
    resetUser();

    toggleDevice();
    await signout(authCtx.tokens.accessToken);
    authDispatchCtx.dispatch({ action: 'clearAuth' });
    router.push('/signin', 'root', 'replace');
    window.location.reload();
  }, [authCtx.tokens.accessToken, authCtx.idData.sub, authDispatchCtx, router]);

  const openDeleteAccountModal = () => {
    setIsDeleteModalOpen(true);
    setIsOpen(false);
  };

  const handleDeleteAccount = useCallback(async () => {
    try {
      // Track account deletion event
      trackEvent('account_deleted', {
        userId: authCtx.idData.sub,
        deletionMethod: 'manual',
      });

      // Reset user in Customer.io
      resetUser();

      await deleteUserData(authCtx.tokens.idToken);
      await deleteUser(authCtx.tokens.accessToken);

      const userId = authCtx.idData.sub;
      await UserRepository.flagUser(userId);

      authDispatchCtx.dispatch({ action: 'clearAuth' });
      router.push('/signin', 'root', 'replace');
    } catch (error) {
      console.error('Error flagging account:', error);
    }
  }, [authCtx.tokens, authCtx.idData.sub, authDispatchCtx, router]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleEditProfile = useCallback(() => {
    closeModal();
    history.push('/app/profile/edit');
  }, [closeModal, history]);

  useImperativeHandle(ref, () => ({
    present: () => setIsOpen(true),
    dismiss: closeModal,
  }));

  return (
    <>
      <IonModal
        breakpoints={[0, 1]}
        initialBreakpoint={1}
        isOpen={isOpen}
        onDidDismiss={closeModal}
        className='settings-modal'>
        <IonHeader className='ion-no-border'>
          <IonToolbar>
            <IonTitle mode='ios'>Settings</IonTitle>
            <IonButtons slot='end'>
              <IonButton onClick={closeModal}>
                <IonIcon slot='icon-only' className='x' icon={closeCircle} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <div className='settings-modal-content ion-padding'>
          <IonItem onClick={handleEditProfile} lines='none'>
            <IonLabel>Profile Setting</IonLabel>
            <IonIcon icon={arrowForwardCircle} slot='end' />
          </IonItem>
          <IonItem onClick={() => setIsContactFormOpen(true)} lines='none'>
            <IonLabel>Contact FoodFight</IonLabel>
            <IonIcon icon={arrowForwardCircle} slot='end' />
          </IonItem>
          <IonItem onClick={() => window.open('https://www.getfoodfight.com/privacy-policy', '_blank')} lines='none'>
            <IonLabel>Privacy Policies</IonLabel>
            <IonIcon icon={arrowForwardCircle} slot='end' />
          </IonItem>
          <IonButton onClick={handleSignout} className='logout-btn' expand='block'>
            <IonLabel>Log Out</IonLabel>
          </IonButton>
          <IonButton onClick={openDeleteAccountModal} className='delete-btn' expand='block'>
            <IonLabel>Delete Account</IonLabel>
          </IonButton>
        </div>
      </IonModal>
      <ContactUsForm isOpen={isContactFormOpen} onClose={() => setIsContactFormOpen(false)} />
      <DeleteAccountModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteAccount}
      />
    </>
  );
}

export default React.forwardRef(SettingsModal);
